/**
 * disableActions
 * -------------
 * Refuerza la app para que se comporte como una "app nativa" cuando se
 * ejecuta dentro de un WebView en Android (Play Store). Bloquea:
 *   - Selección de texto (mouse / long-press).
 *   - Menú contextual (click derecho / mantener pulsado).
 *   - Copiar / Cortar / Pegar desde fuera o hacia fuera.
 *   - Arrastre de imágenes / elementos fuera del WebView.
 *   - Gestos de zoom (pinch) en trackpads.
 *   - Zoom con Ctrl + rueda / Ctrl +/- en desktop.
 *   - Apertura de DevTools (F12).
 *   - Drop de archivos externos.
 *   - Selección por teclado (Ctrl+A, etc.).
 *
 * Se ejecuta como efecto secundario (sin imports) y se autodispara al
 * cargarse el módulo, por lo que basta importarlo desde index.js para que
 * esté activo antes que React monte.
 */

const ANTI_SELECTION_CSS = `
html, body, #app, #app * {
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
  user-select: none !important;
  -webkit-touch-callout: none !important;
  -webkit-tap-highlight-color: transparent !important;
  -webkit-user-drag: none !important;
  user-drag: none !important;
}
input, input[type="text"], input[type="email"], input[type="password"],
input[type="number"], input[type="tel"], input[type="search"],
input[type="url"], textarea, [contenteditable="true"], [contenteditable=""] {
  -webkit-user-select: text !important;
  -moz-user-select: text !important;
  -ms-user-select: text !important;
  user-select: text !important;
  -webkit-touch-callout: default !important;
}
img, svg, video, canvas, audio {
  -webkit-user-drag: none !important;
  user-drag: none !important;
  pointer-events: auto !important;
}
::selection, ::-moz-selection {
  background: transparent !important;
}
* {
  -webkit-touch-callout: none !important;
}
`;

let booted = false;

const injectCssOnce = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("__dpro_disable_actions__")) return;
  const tag = document.createElement("style");
  tag.id = "__dpro_disable_actions__";
  tag.appendChild(document.createTextNode(ANTI_SELECTION_CSS));
  if (document.head) {
    document.head.appendChild(tag);
  } else {
    document.documentElement.appendChild(tag);
  }
};

const stop = (e) => {
  if (!e) return;
  try {
    e.preventDefault();
    e.stopPropagation();
  } catch (_) {
    /* noop */
  }
};

const isEditableTarget = (target) => {
  if (!target) return false;
  const tag = (target.tagName || "").toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (target.isContentEditable) return true;
  return false;
};

const onCopy = (e) => {
  if (isEditableTarget(e.target)) return;
  stop(e);
  try {
    if (e.clipboardData) e.clipboardData.setData("text/plain", "");
  } catch (_) {
    /* noop */
  }
};

const onSelectionKeys = (e) => {
  const k = (e.key || "").toLowerCase();
  const isCtrl = e.ctrlKey || e.metaKey;
  if (!isCtrl) return;
  if (k === "a" || k === "c" || k === "x" || k === "v" || k === "p" ||
      k === "s" || k === "u" || k === "i" || k === "j") {
    if (isEditableTarget(e.target) && (k === "c" || k === "x" || k === "v" || k === "a")) {
      return;
    }
    stop(e);
  }
};

const onWheel = (e) => {
  if (e.ctrlKey) stop(e);
};

const onKeydown = (e) => {
  if ((e.ctrlKey || e.metaKey) &&
      (e.key === "+" || e.key === "-" || e.key === "0" || e.key === "=")) {
    stop(e);
  }
  if (e.key === "F12") stop(e);
};

const onContextMenu = (e) => {
  if (isEditableTarget(e.target)) return;
  stop(e);
};

const onSelectStart = (e) => {
  if (isEditableTarget(e.target)) return;
  stop(e);
};

const onDragStart = (e) => {
  const node = e.target;
  const tag = (node && node.tagName || "").toLowerCase();
  if (tag === "img" || tag === "a") stop(e);
};

const onDrop = (e) => stop(e);

const onGestureStart = (e) => stop(e);

const bindOnce = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (booted) return;
  booted = true;

  const opts = { capture: true, passive: false };
  document.addEventListener("copy", onCopy, opts);
  document.addEventListener("cut", onCopy, opts);
  document.addEventListener("paste", (e) => {
    if (isEditableTarget(e.target)) return;
    stop(e);
  }, opts);
  document.addEventListener("selectstart", onSelectStart, opts);
  document.addEventListener("contextmenu", onContextMenu, opts);
  document.addEventListener("dragstart", onDragStart, opts);
  document.addEventListener("drop", onDrop, opts);
  document.addEventListener("gesturestart", onGestureStart, opts);
  document.addEventListener("wheel", onWheel, { capture: true, passive: false });
  document.addEventListener("keydown", (e) => {
    onSelectionKeys(e);
    onKeydown(e);
  }, opts);

  window.addEventListener("dragstart", onDragStart);
  window.addEventListener("contextmenu", onContextMenu);
};

const disableActions = () => {
  injectCssOnce();
  bindOnce();
};

disableActions();

export default disableActions;
